import { useState, useEffect, useRef } from 'react'
import { X, Save } from 'lucide-react'
import { Modal, Button } from '@/components/common'
import { API_BASE_URL, API_ENDPOINTS } from '@/constants'
import { storageService } from '@/services'

const CATTLE_TYPES = [
  { value: 'BREEDING_COW', label: 'แม่พันธุ์' },
  { value: 'PREGNANT_COW', label: 'แม่ท้อง' },
  { value: 'BREEDING_BULL', label: 'พ่อพันธุ์' },
  { value: 'FATTENING_CATTLE', label: 'โคขุน' },
  { value: 'CALF', label: 'ลูกโค' },
  { value: 'YOUNG_CATTLE', label: 'โครุ่น' },
]

interface FeedingRecord {
  id: string
  date: string
  cattleType: string
  roughage: { weight: number; dryMatter: number }
  concentrate: { weight: number; dryMatter: number }
  tmr: { weight: number; dryMatter: number }
  notes?: string
}

interface FeedingFormModalProps {
  isOpen: boolean
  onClose: () => void
  onSave: (data: FeedingRecord, selectedDate: Date) => void
  selectedDate: Date
  editData?: FeedingRecord | null
  defaultCattleType?: string
  farmId: string
}

export default function FeedingFormModal({
  isOpen,
  onClose,
  onSave,
  selectedDate,
  editData,
  defaultCattleType = '',
  farmId,
}: FeedingFormModalProps) {
  const [formDate, setFormDate] = useState(selectedDate)
  const [formData, setFormData] = useState({
    cattleType: editData?.cattleType || defaultCattleType,
    roughageWeight: editData?.roughage.weight || 0,
    roughageDM: editData?.roughage.dryMatter || 85,
    concentrateWeight: editData?.concentrate.weight || 0,
    concentrateDM: editData?.concentrate.dryMatter || 90,
    tmrWeight: editData?.tmr.weight || 0,
    tmrDM: editData?.tmr.dryMatter || 50,
    notes: editData?.notes || '',
  })
  const [dateRecords, setDateRecords] = useState<FeedingRecord[]>([])
  const prevCattleTypeRef = useRef(formData.cattleType)

  // Update form date when selectedDate prop changes
  useEffect(() => {
    setFormDate(selectedDate)
  }, [selectedDate])

  // Fetch records for the selected date
  useEffect(() => {
    if (farmId && formDate) {
      fetchRecordsForDate(formDate)
    }
  }, [formDate, farmId])

  const fetchRecordsForDate = async (date: Date) => {
    try {
      const token = storageService.getAuthToken()
      const dateStr = date.toISOString().split('T')[0]

      const response = await fetch(
        `${API_BASE_URL}${API_ENDPOINTS.FEEDING.BASE}?farmId=${farmId}&date=${dateStr}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      )

      if (response.ok) {
        const data = await response.json()
        const records = data.map((record: any) => ({
          id: record.id,
          date: record.date.split('T')[0],
          cattleType: record.cattleType,
          roughage: { weight: record.roughageWeight, dryMatter: record.roughageDryMatter },
          concentrate: {
            weight: record.concentrateWeight,
            dryMatter: record.concentrateDryMatter,
          },
          tmr: { weight: record.tmrWeight, dryMatter: record.tmrDryMatter },
          notes: record.notes || '',
        }))
        setDateRecords(records)
      }
    } catch (error) {
      console.error('Error fetching feeding records for date:', error)
    }
  }

  useEffect(() => {
    if (editData) {
      setFormData({
        cattleType: editData.cattleType,
        roughageWeight: editData.roughage.weight,
        roughageDM: editData.roughage.dryMatter,
        concentrateWeight: editData.concentrate.weight,
        concentrateDM: editData.concentrate.dryMatter,
        tmrWeight: editData.tmr.weight,
        tmrDM: editData.tmr.dryMatter,
        notes: editData.notes || '',
      })
    }
  }, [editData])

  // Load data when dateRecords changes (from date change)
  useEffect(() => {
    if (formData.cattleType && dateRecords.length > 0) {
      const existingRecord = dateRecords.find(
        (record) => record.cattleType === formData.cattleType
      )

      if (existingRecord) {
        // Load existing data for this cattle type
        setFormData((prev) => ({
          ...prev,
          roughageWeight: existingRecord.roughage.weight,
          roughageDM: existingRecord.roughage.dryMatter,
          concentrateWeight: existingRecord.concentrate.weight,
          concentrateDM: existingRecord.concentrate.dryMatter,
          tmrWeight: existingRecord.tmr.weight,
          tmrDM: existingRecord.tmr.dryMatter,
          notes: existingRecord.notes || '',
        }))
      } else {
        // Reset to default if no record found for this cattle type
        setFormData((prev) => ({
          ...prev,
          roughageWeight: 0,
          roughageDM: 85,
          concentrateWeight: 0,
          concentrateDM: 90,
          tmrWeight: 0,
          tmrDM: 50,
          notes: '',
        }))
      }
    } else if (formData.cattleType && dateRecords.length === 0) {
      // Reset when no records for selected date at all
      setFormData((prev) => ({
        ...prev,
        roughageWeight: 0,
        roughageDM: 85,
        concentrateWeight: 0,
        concentrateDM: 90,
        tmrWeight: 0,
        tmrDM: 50,
        notes: '',
      }))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateRecords])

  // Reload data when cattleType is manually changed
  useEffect(() => {
    const cattleTypeChanged = prevCattleTypeRef.current !== formData.cattleType

    if (cattleTypeChanged) {
      prevCattleTypeRef.current = formData.cattleType

      if (formData.cattleType) {
        const existingRecord = dateRecords.find(
          (record) => record.cattleType === formData.cattleType
        )

        if (existingRecord) {
          setFormData((prev) => ({
            ...prev,
            roughageWeight: existingRecord.roughage.weight,
            roughageDM: existingRecord.roughage.dryMatter,
            concentrateWeight: existingRecord.concentrate.weight,
            concentrateDM: existingRecord.concentrate.dryMatter,
            tmrWeight: existingRecord.tmr.weight,
            tmrDM: existingRecord.tmr.dryMatter,
            notes: existingRecord.notes || '',
          }))
        } else {
          // Reset to default when changing to cattle type with no data
          setFormData((prev) => ({
            ...prev,
            roughageWeight: 0,
            roughageDM: 85,
            concentrateWeight: 0,
            concentrateDM: 90,
            tmrWeight: 0,
            tmrDM: 50,
            notes: '',
          }))
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.cattleType])

  const handleChange = (field: string, value: string | number) => {
    setFormData((prev) => ({ ...prev, [field]: value }))
  }

  const handleCattleTypeChange = (newCattleType: string) => {
    // Just update cattleType, useEffect will handle loading data
    setFormData((prev) => ({ ...prev, cattleType: newCattleType }))
  }

  const calculateTotalWeight = () => {
    return formData.roughageWeight + formData.concentrateWeight + formData.tmrWeight
  }

  const calculateTotalDryMatter = () => {
    const roughageDM = (formData.roughageWeight * formData.roughageDM) / 100
    const concentrateDM = (formData.concentrateWeight * formData.concentrateDM) / 100
    const tmrDM = (formData.tmrWeight * formData.tmrDM) / 100
    return roughageDM + concentrateDM + tmrDM
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()

    if (!formData.cattleType) {
      alert('กรุณาเลือกกลุ่มวัว')
      return
    }

    // ตรวจสอบว่ามีข้อมูลของวันที่และกลุ่มวัวนี้อยู่แล้วหรือไม่
    const dateStr = formDate.toISOString().split('T')[0]
    const existingRecord = dateRecords.find(
      (record) => record.cattleType === formData.cattleType
    )

    const record: FeedingRecord = {
      id: existingRecord?.id || editData?.id || `temp-${Date.now()}`,
      date: dateStr,
      cattleType: formData.cattleType,
      roughage: {
        weight: formData.roughageWeight || 0,
        dryMatter: formData.roughageDM || 0,
      },
      concentrate: {
        weight: formData.concentrateWeight || 0,
        dryMatter: formData.concentrateDM || 0,
      },
      tmr: {
        weight: formData.tmrWeight || 0,
        dryMatter: formData.tmrDM || 0,
      },
      notes: formData.notes,
    }

    onSave(record, formDate)
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-semibold text-text-primary">
          {editData ? 'แก้ไขการบันทึกอาหาร' : 'บันทึกการให้อาหาร'}
        </h2>
        <button
          onClick={onClose}
          className="text-text-tertiary hover:text-text-primary transition-colors"
        >
          <X className="w-6 h-6" />
        </button>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Date & Cattle Type */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-text-primary mb-2">
              วันที่ <span className="text-error">*</span>
            </label>
            <input
              type="date"
              value={formDate.toISOString().split('T')[0]}
              onChange={(e) => setFormDate(new Date(e.target.value))}
              required
              className="w-full px-4 py-2 border border-border-medium rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-800"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-text-primary mb-2">
              กลุ่มวัว <span className="text-error">*</span>
            </label>
            <select
              value={formData.cattleType}
              onChange={(e) => handleCattleTypeChange(e.target.value)}
              required
              className="w-full px-4 py-2 border border-border-medium rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-800"
            >
              <option value="">-- เลือกกลุ่มวัว --</option>
              {CATTLE_TYPES.map((type) => (
                <option key={type.value} value={type.value}>
                  {type.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Feed Section: Roughage */}
        <div className="border border-border-light rounded-lg p-4 bg-green-50">
          <h3 className="font-semibold text-green-700 mb-4 flex items-center gap-2">
            🌾 อาหารหยาบ (Roughage)
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-text-primary mb-2">
                น้ำหนัก (กก.)
              </label>
              <input
                type="number"
                step="0.1"
                min="0"
                value={formData.roughageWeight}
                onChange={(e) => handleChange('roughageWeight', parseFloat(e.target.value) || 0)}
                className="w-full px-4 py-2 border border-border-medium rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                placeholder="0"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-text-primary mb-2">
                % น้ำหนักแห้ง (DM)
              </label>
              <input
                type="number"
                step="0.1"
                min="0"
                max="100"
                value={formData.roughageDM}
                onChange={(e) => handleChange('roughageDM', parseFloat(e.target.value) || 0)}
                className="w-full px-4 py-2 border border-border-medium rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                placeholder="85"
              />
            </div>
          </div>
          <p className="text-xs text-text-tertiary mt-2">
            น้ำหนักแห้ง:{' '}
            <span className="font-semibold text-green-700">
              {((formData.roughageWeight * formData.roughageDM) / 100).toFixed(2)} กก.
            </span>
          </p>
        </div>

        {/* Feed Section: Concentrate */}
        <div className="border border-border-light rounded-lg p-4 bg-amber-50">
          <h3 className="font-semibold text-amber-700 mb-4 flex items-center gap-2">
            🌽 อาหารข้น (Concentrate)
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-text-primary mb-2">
                น้ำหนัก (กก.)
              </label>
              <input
                type="number"
                step="0.1"
                min="0"
                value={formData.concentrateWeight}
                onChange={(e) =>
                  handleChange('concentrateWeight', parseFloat(e.target.value) || 0)
                }
                className="w-full px-4 py-2 border border-border-medium rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500"
                placeholder="0"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-text-primary mb-2">
                % น้ำหนักแห้ง (DM)
              </label>
              <input
                type="number"
                step="0.1"
                min="0"
                max="100"
                value={formData.concentrateDM}
                onChange={(e) => handleChange('concentrateDM', parseFloat(e.target.value) || 0)}
                className="w-full px-4 py-2 border border-border-medium rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500"
                placeholder="90"
              />
            </div>
          </div>
          <p className="text-xs text-text-tertiary mt-2">
            น้ำหนักแห้ง:{' '}
            <span className="font-semibold text-amber-700">
              {((formData.concentrateWeight * formData.concentrateDM) / 100).toFixed(2)} กก.
            </span>
          </p>
        </div>

        {/* Feed Section: TMR */}
        <div className="border border-border-light rounded-lg p-4 bg-blue-50">
          <h3 className="font-semibold text-blue-700 mb-4 flex items-center gap-2">
            🥗 TMR (Total Mixed Ration)
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-text-primary mb-2">
                น้ำหนัก (กก.)
              </label>
              <input
                type="number"
                step="0.1"
                min="0"
                value={formData.tmrWeight}
                onChange={(e) => handleChange('tmrWeight', parseFloat(e.target.value) || 0)}
                className="w-full px-4 py-2 border border-border-medium rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="0"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-text-primary mb-2">
                % น้ำหนักแห้ง (DM)
              </label>
              <input
                type="number"
                step="0.1"
                min="0"
                max="100"
                value={formData.tmrDM}
                onChange={(e) => handleChange('tmrDM', parseFloat(e.target.value) || 0)}
                className="w-full px-4 py-2 border border-border-medium rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="50"
              />
            </div>
          </div>
          <p className="text-xs text-text-tertiary mt-2">
            น้ำหนักแห้ง:{' '}
            <span className="font-semibold text-blue-700">
              {((formData.tmrWeight * formData.tmrDM) / 100).toFixed(2)} กก.
            </span>
          </p>
        </div>

        {/* Summary */}
        <div className="bg-primary-50 border border-primary-200 rounded-lg p-4">
          <h3 className="font-semibold text-primary-800 mb-3">สรุปรวม</h3>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-sm text-text-secondary">น้ำหนักรวมทั้งหมด</p>
              <p className="text-2xl font-bold text-primary-800">
                {calculateTotalWeight().toFixed(1)} กก.
              </p>
            </div>
            <div>
              <p className="text-sm text-text-secondary">น้ำหนักแห้งรวม (DM)</p>
              <p className="text-2xl font-bold text-primary-800">
                {calculateTotalDryMatter().toFixed(1)} กก.
              </p>
            </div>
          </div>
        </div>

        {/* Notes */}
        <div>
          <label className="block text-sm font-medium text-text-primary mb-2">หมายเหตุ</label>
          <textarea
            value={formData.notes}
            onChange={(e) => handleChange('notes', e.target.value)}
            rows={3}
            className="w-full px-4 py-2 border border-border-medium rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-800 resize-none"
            placeholder="บันทึกเพิ่มเติม..."
          />
        </div>

        {/* Actions */}
        <div className="flex items-center justify-end gap-3 pt-4 border-t border-border-light">
          <Button type="button" variant="secondary" onClick={onClose}>
            ยกเลิก
          </Button>
          <Button type="submit" variant="primary" className="flex items-center gap-2">
            <Save className="w-4 h-4" />
            <span>{editData ? 'บันทึกการแก้ไข' : 'บันทึก'}</span>
          </Button>
        </div>
      </form>
    </Modal>
  )
}
